import React, { createContext, Dispatch, ReactElement, ReactNode, RefObject, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import {Device, types as MediasoupTypes} from "mediasoup-client"
import { InitialiseConnection, InitialiseProducerTransportListener, InitialiseScreenTransportListener,  InitialiseUpdateProducerTransportListener,  onConsumerTransportCreated, onProducerTransportCreated, sendRequest } from "../websocket/websocket";
import { FETCH_POLICY_NO_CACHE, PTA_ROLE, SCHOOL_ADMIN, STUDENT_ROLE, TEACHER_ROLE, YOU_ARE_PRESENTING_NOW } from "../utils/constants";
import { getDisplayMedia } from "../utils/webRTCHelper";
import ToasterWithMessage from "../components/toaster";
import { useLazyQuery, useMutation } from "@apollo/client";
import { CREATE_MEDIASERVER_ERROR_LOG, GET_ERROR_DATA_BY_ID, UPDATE_ERROR_DATA } from "../pages/LiveClass/api/LiveClassQuery";

export type ProfileTileData = {
    userId: string, userName: string, avatarCode: string, preference: {audio: boolean, video: boolean, hand: boolean}, videoStream: MediaStream | null, audioStream: MediaStream | null
}

type GlobalContextType = {
    InitialiseMeeting: (data: MeetStateType, meetType: FormType) => void
    meetStateRef: RefObject<MeetStateType | undefined>   
    isHost: boolean  
    profileData: ProfileTileData | null
    localStream: MediaStream | null 
    participants: ParticipantType | null
    audio: boolean
    toggleAudio: (audio: boolean) => void
    video: boolean
    toggleVideo: (video: boolean) => void
    hand: boolean
    toggleHandRaise: (hand: boolean) => void
    muteUsers: (userKey: string) => void
    muteAllUsers: () => void
    dialogOpen: boolean
    setDialogOpen: Dispatch<SetStateAction<boolean>>
    presenter: MainTileType | null
    pinVideoToMainTile: (data: PinVideoType) => void
    startScreenShare: () => void
    displayStreamRef: RefObject<MediaStream | null>
    chatMessages: MessageType | null
    sendMessage: (message: string) => void
    isChatOpen: boolean
    isParticipantListView: boolean
    toggleParticipant: (isOpen: boolean) => void
    isParticipantsGridViewVisible: boolean;
    handleParticipantsGridViewToggle: (isVisible: boolean) => void;
    toggleChat: (isOpen: boolean) => void
    leaveMeeting: any
    isScreenSharing: boolean 
    isAnyoneScreenSharing: boolean
    isRecording: boolean
    handleScreenShare: () => void
    stopScreenShare: () => void
    setOpenShareScreenPopup: (val: boolean) => void
    openShareScreenPopup: boolean
    videoRecordUploaded: boolean
    setVideoRecordUploaded: (val: boolean) => void
    uploadedFileName: string
    endMeeting: any;
    presenterRef: any;
    setPresenter: any;
    isMeetingEndByHost: boolean;
    setParticipants: any;
    participantsRef: any;
    setProfileData: any;
    setIsMeetingEndByHost: (val: boolean) => void;
    startRecording: () => void
    stopRecording: () => void
    recordingByUser:any
    recordingStartedTime:any
    isOnline: any;
}

export type MainTileType = {
    userName: String, 
    userId: String, 
    videoStream: MediaStream | null,
    title: string,
    pinType:{
        screen: boolean,
        video: boolean
    } ,
    preference: {
        audio: boolean,
        video: boolean,
        screen: boolean,
        hand: boolean
    }
}

type PinVideoType = {
    pin: boolean, 
    userKey: string,
    userId: string,
    userName: string, 
    videoStream: MediaStream | null, 
    isCurrentUser: boolean, 
    type: string,
}

type MeetStateType = {
    meetId: string,
    createdAt: string,
    avatar: string,
    currentUser: {
        userId: string,
        userName: string,
        userKey: string,
        avatarKey: string,
        contentShareKey?: string,
        contentShareId?: string,
        isPinned: {screen: boolean, video: boolean},
        preference: {
            audio: boolean,
            video: boolean,
            screen: boolean,
            hand: boolean
        }
    },
    host: {
        hostId: string,
        hostName: string,
        hostKey: string
    }
}

export interface Participant {
    userId: string;
    userName: string;
    userKey: string;
    avatar: string;
    avatarKey: string;
    isPinned: {
        video: boolean;
        screen: boolean;
    };
    preference: {
        audio: boolean;
        video: boolean;
        screen: boolean;
        hand: boolean;
    };
    remoteStream: {
        video?: MediaStream | null;
        audio?: MediaStream | null;
    };
    screenShare: {
        displayStream?: MediaStream;
        screenId?: string;
    };
    producer: {
        videoId: string;
        audioId: string;
    };
    updatedAt?: number;
}

export type ParticipantType = {
    [key: string]: Participant;
};

type MessageType = {
    [key: string]: {
        userKey: string,
        userName: string,
        message: string,
        createdAt: string,
        read:boolean
    }
}

export enum FormType {
    CREATE = 'create',
    JOIN = 'join'
}

export const GlobalContext = createContext<GlobalContextType | null>(null);

export function useGlobalState():GlobalContextType {
    const context = useContext(GlobalContext);
    if (!context) {
        throw new Error("useGlobalContext must be used within an GlobalProvider");
    }
    return context;
}

export const GlobalProvider = (props: {children: ReactNode}):ReactElement => {

    const meetStateRef = useRef<MeetStateType | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const [presenter, setPresenter] = useState<MainTileType | null>(null); //Main Tile
    const presenterRef = useRef<MainTileType | null>(null);
    const { error } = ToasterWithMessage();
    const [openShareScreenPopup, setOpenShareScreenPopup] = useState<boolean>(false);
    const [isHost, setHost] = useState<boolean>(false);



    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    const participantsRef = useRef<ParticipantType | null>(null);
    const [participants, setParticipants] = useState<ParticipantType | null>(null);

    const [audio, setAudio] = useState<boolean>(true);
    const [video, setVideo] = useState<boolean>(true);
    const [hand, setHand] = useState<boolean>(false);

    const consumerTransport = useRef<MediasoupTypes.Transport | null>(null);
    const producerTransport = useRef<MediasoupTypes.Transport | null>(null);
    const screenTransport = useRef<MediasoupTypes.Transport | null>(null);

    const [localStream, setLocalStream] = useState<MediaStream | null>(null);

    const [isScreenSharing, setIsScreenSharing] = useState<boolean>(false);
    const [isAnyoneScreenSharing, setIsAnyoneScreenSharing] = useState<boolean>(false);
    // const [displayStream, setDisplayStream] = useState<MediaStream>();
    const displayStreamRef = useRef<MediaStream | null>(null);

    const chatMessageRef = useRef<MessageType | null>(null);
    const [chatMessages, setChatMessage] = useState<MessageType | null>(null);

    const deviceRef = useRef<Device>();
    const [isRecording, setIsRecording] = useState<boolean>(false); //new line
    const [recordingStartedTime, setRecordingStartedTime] = useState();
    const [recordingByUser, setRecordingByUser] = useState<any>();
    const [videoRecordUploaded, setVideoRecordUploaded] = useState<boolean>(false);
    const [uploadedFileName, setUploadedFileName] = useState<string>('');

    const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
    const isChatOpenRef = useRef(isChatOpen);

    const [isParticipantListView, setIsParticipantListView]= useState<boolean>(false);
    const [profileData, setProfileData] = useState<ProfileTileData | null>(null);
    
    const [isParticipantsGridViewVisible, setParticipantsGridViewVisible] = useState(false);
    const [isMeetingEndByHost, setIsMeetingEndByHost] = useState<boolean>(false);

    // const [socketExists, setSocketExists] = useState<boolean>(false);
    const socketExistsRef = useRef<boolean>(false);
    const producerDisconnectedRef = useRef<boolean>(false);
    const consumerDisconnectedRef = useRef<boolean>(false);
    const screenDisconnectedRef = useRef<boolean>(false);
    const routerRtpCapabilitiesref = useRef<any>("");
    const [isOnline, setIsOnline] = useState<boolean>(true);
    
    const [insertSendRequest] = useMutation(CREATE_MEDIASERVER_ERROR_LOG, {
        fetchPolicy: FETCH_POLICY_NO_CACHE
    });

    const [getErrorDataById] = useLazyQuery(GET_ERROR_DATA_BY_ID, {
        fetchPolicy: FETCH_POLICY_NO_CACHE
    });

    const [updateErrorData] = useMutation(UPDATE_ERROR_DATA, {
        fetchPolicy: FETCH_POLICY_NO_CACHE
    });
    
    useEffect(() => {
        isChatOpenRef.current = isChatOpen;
    }, [isChatOpen]);

    useEffect(() => {
        if(presenter){
            setIsAnyoneScreenSharing(true)
        } else{
            setIsAnyoneScreenSharing(false)
        }
    },[presenter])

    useEffect(() => {
        console.log("inside useEffect -----",meetStateRef.current);
        if(meetStateRef.current && meetStateRef.current.currentUser.userId === meetStateRef.current.host.hostId){
                setProfileData({
                    userId: meetStateRef.current.currentUser.userId,
                    userName: meetStateRef.current.currentUser.userName,
                    avatarCode: meetStateRef.current.avatar,
                    preference: meetStateRef.current.currentUser.preference,
                    videoStream: localStream,
                    audioStream: localStream
                });
        }else if(participants){
            let profileData: ProfileTileData | null = null;
            Object.keys(participants).forEach( (key) => {
                const user:any = participants[key];
                if(user.userId === meetStateRef.current?.host.hostId){
                    profileData = {
                        userId: user.userId,
                        userName: user.userName,
                        avatarCode: user.avatar,
                        preference: user.preference,
                        videoStream: user.remoteStream.video,
                        audioStream: user.remoteStream.audio
                    };
                }
            });
            if(profileData){
                setProfileData(profileData)
            }
            else if(meetStateRef.current){
                setProfileData({
                    userId: meetStateRef.current.currentUser.userId,
                    userName: meetStateRef.current.currentUser.userName,
                    avatarCode: meetStateRef.current.avatar,
                    preference: meetStateRef.current.currentUser.preference,
                    videoStream: localStream,
                    audioStream: localStream
            });
            }   
        }
    }, [participants, meetStateRef.current, localStream, video]);

    const handleParticipantsGridViewToggle = (isVisible: boolean) => {
        setParticipantsGridViewVisible(isVisible);
    }

    const toggleChat = (isOpen: boolean) => {
        setIsChatOpen(isOpen);
        if (isOpen) {
            const updatedMessages = { ...chatMessageRef.current };
            for (const key in updatedMessages) {
                if (updatedMessages[key]) {
                    updatedMessages[key].read = true;
                }
            }
            chatMessageRef.current = updatedMessages;
            setChatMessage(updatedMessages);
        }
    };

    const toggleParticipant = (isOpen: boolean) => {
        setIsParticipantListView(isOpen)
    }

    const resetAllValues = async (role?: any, state?: any, navigate?: any, meetComponent?: any, locationState?: any) => {
        if(socketRef.current && meetStateRef.current){
            setParticipantsGridViewVisible(false);
            if(localStream?.getVideoTracks()[0]){
                localStream.getVideoTracks()[0].enabled = false;
            }
            localStream?.getVideoTracks()[0]?.stop();
            if(localStream?.getAudioTracks()[0]){
                localStream.getAudioTracks()[0].enabled = false;
            }
            localStream?.getAudioTracks()[0]?.stop();
            socketRef.current.close();
            socketRef.current = null;
            meetStateRef.current = null;
            participantsRef.current = null;
            chatMessageRef.current = null;
            setIsRecording(false);
            setChatMessage(null);
            setVideo(true);
            setAudio(true);
            setHand(false);
            setParticipants(participantsRef.current);
            setIsChatOpen(false);
            setIsParticipantListView(false);
            displayStreamRef.current = null;
            setVideoRecordUploaded(false);
            setUploadedFileName('');
            setProfileData(null);   
            socketExistsRef.current = false;
            producerDisconnectedRef.current = false;
            consumerDisconnectedRef.current = false;
            screenDisconnectedRef.current = false;
            producerTransport.current?.close();
            consumerTransport.current?.close();
            screenTransport.current?.close();
           if(role && meetComponent){
                if(role === STUDENT_ROLE){
                    navigate('/student-classroom/liveclass',{state:state});
                }else if(role === TEACHER_ROLE && meetComponent === 'liveclass'){
                    navigate('/teacher-classroom/liveclass',{state:state});
                }else if(role === TEACHER_ROLE && meetComponent === 'workshop'){
                    navigate('/teacher-workshop');
                }else if(role === TEACHER_ROLE && meetComponent === 'pta'){
                    navigate('/teacher-pta', {
                        state: locationState
                      });
                }else if(role === SCHOOL_ADMIN && meetComponent === 'pta'){
                    navigate('/admin-pta/meetings', {
                        state: locationState
                      });
                }else if(role === SCHOOL_ADMIN && meetComponent === 'workshop'){
                    navigate('/admin-workshop');
                }else if(role === PTA_ROLE) {
                    navigate('/pta-meetings', {
                        state: locationState
                      });
                }
           }
        }
    }
    
    const leaveMeeting = async (role?:string, state?:any, navigate?:any, meetComponent?:string, locationState?: any) => {
        if(socketRef.current && meetStateRef.current){
            await stopScreenShare();
            presenterRef.current = null;
            setPresenter(presenterRef.current);
            setTimeout(() => {
                if(socketRef.current && meetStateRef.current) {
                    sendRequest(socketRef.current, "leaveMeeting", {
                        meetId: meetStateRef.current?.meetId, 
                        userKey: meetStateRef.current.currentUser.userKey, 
                        userId: meetStateRef.current.currentUser.userId,
                    });
                }
            }, 1000)
            // window.location.reload();
            console.log("localStream?.getVideoTracks()[0]", localStream?.getVideoTracks(), localStream?.getVideoTracks()[0]);
            console.log("localStream?.getAudioTracks()[0]", localStream?.getAudioTracks(), localStream?.getAudioTracks()[0]);
            setTimeout(() => {
                resetAllValues(role, state, navigate, meetComponent, locationState);
            },1000)
        }

    }


    const InitialiseMeeting = async(data: MeetStateType, meetType: FormType) => {
        meetStateRef.current = data;
        if(data.currentUser.userId === data.host.hostId){
            setHost(true);
        }
        const {newSocket, errorMessage} = await InitialiseConnection();
        if(newSocket){
            socketRef.current = newSocket;
            InitSocket(meetType);
        }else{
            error(errorMessage);
        }
        
    }

    const toggleAudio = (audio: boolean) => {
        if(socketRef.current && meetStateRef.current){
            sendRequest(socketRef.current, "toggleAudio", {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey, audio});
        }
    }

    console.log("the videotract outside", localStream?.getVideoTracks()[0]);
    const toggleVideo = async(video: boolean) => {
        if(socketRef.current && meetStateRef.current){
            sendRequest(socketRef.current, "toggleVideo", {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey, video});
        }
        if(video && producerTransport.current && socketRef.current && meetStateRef.current){
            const device = new Device();
            sendRequest(socketRef.current, "updateProducerTransport", {
                forceTcp: false, 
                rtpCapabilities: device,
                meetId: meetStateRef.current.meetId, 
                userKey: meetStateRef.current.currentUser.userKey
            });
        }else{
            if(localStream){
                localStream.getVideoTracks()[0].stop();
                localStream.getVideoTracks()[0].enabled = false;
                setLocalStream(null);
            }
        }
    }

    const toggleHandRaise = async(hand: boolean) => {
        if(socketRef.current && meetStateRef.current){
            sendRequest(socketRef.current, "toggleHandRaise", {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey, hand});
        }
    }

    const muteUsers = (userKey: string) => {
        if(socketRef.current && meetStateRef.current && isHost){
            sendRequest(socketRef.current, "toggleAudio", {meetId: meetStateRef.current.meetId, userKey, audio: false});
        }
    }

    const muteAllUsers = () => {
        if(socketRef.current && meetStateRef.current && isHost){
            sendRequest(socketRef.current, "muteAllUsers", {meetId: meetStateRef.current.meetId, userId: meetStateRef.current.currentUser.userId});
        }
    }
    
    const pinVideoToMainTile = (data: PinVideoType) => {
        const {pin, userId, userKey, userName, isCurrentUser, videoStream, type} = data;
        if(isCurrentUser){
            if(meetStateRef.current){
                if(pin){
                    const presenter = {
                        userId,
                        userName,
                        videoStream,
                        title: type === "screen" ? YOU_ARE_PRESENTING_NOW :  `You (${userName})`,
                        pinType: type === "screen" ? {screen: pin, video: false} : {screen: false, video: pin},
                        preference: meetStateRef.current.currentUser.preference
                    }
                    setPresenter(presenter);
                    presenterRef.current = presenter;
                }
                    
                
                meetStateRef.current.currentUser.isPinned = (type === "screen") ? {screen: pin, video: false}  : {screen: false , video: pin};
            }
            if(participants){
                Object.keys(participants).forEach((key) => {
                    const user = participants[key];
                    user.isPinned = {
                        video: false,
                        screen: false
                    }
                });
            }
        }else{
            if(participants){
                Object.keys(participants).forEach((key) => {
               
                    if(key === userKey){
                        participants[key].isPinned = (type === "screen") ? {video: false, screen: pin} : {video: pin, screen: false};
                        if(pin){
                            const presenter = {
                                userId,
                                userName,
                                videoStream,
                                title: type === "screen" ? `${userName} is presenting now.` : `${userName}`,
                                pinType: type === "screen" ? {video: false, screen: pin} : {video: pin, screen: false},
                                preference: participants[key].preference
                            }
                            presenterRef.current = presenter
                            setPresenter(presenter);
                        }
                            
                    }
                });
            }
            
            if(meetStateRef.current){
                meetStateRef.current.currentUser.isPinned = {
                    video: false,
                    screen: false
                }
            }
        }
        if(!pin){
            setPresenter(null);
            presenterRef.current = null;
        }
        
    }

    const updatePresenter = (data: {userId: string, userKey: string, preference: {[key: string] : boolean}}) => {
        const {userId, preference} = data;
       
        if(presenterRef.current && presenterRef.current.userId === userId){
            const update = {...presenterRef.current};
            const key = Object.keys(preference)[0];
            switch(key){
                case "video": 
                    update.preference.video = preference[key];
                    setPresenter(update);
                    presenterRef.current = update;
                break;  
            }
        }
    }

    const handleScreenShare = async() => {
        if(socketRef.current) {
            sendRequest(socketRef.current, "getPresenters", { 
                meetId: meetStateRef.current?.meetId, 
                userKey: meetStateRef.current?.currentUser.userKey, 
                userName: meetStateRef.current?.currentUser.userName
            });
        }
    }

    const startScreenShare = async() => {
        const transport = screenTransport.current;
        await getDisplayMedia({video: true, audio: false}).then( async(displayStream) => {
            if(displayStream && meetStateRef.current){
                setDialogOpen(false);
                setParticipantsGridViewVisible(false);
                displayStreamRef.current = displayStream;
                const presenter = {
                    userId: meetStateRef.current.currentUser.userId,
                    userName: meetStateRef.current.currentUser.userName,
                    videoStream: displayStream,
                    title: YOU_ARE_PRESENTING_NOW,
                    pinType: {
                        screen: true,
                        video: false
                    },
                    preference: meetStateRef.current.currentUser.preference
                }
                presenterRef.current = presenter;
                setPresenter(presenter);
                if(transport){
                    await transport.produce({track: displayStream.getVideoTracks()[0]});
                }
                displayStream.getVideoTracks()[0].onended = () => {
                    stopScreenShare();
                }
            }
        });
    }

    const stopScreenShare = async() => {
        if(socketRef.current && meetStateRef.current && displayStreamRef.current){
            displayStreamRef.current.getVideoTracks()[0].stop();
            displayStreamRef.current = null;
            setIsScreenSharing(false);
            sendRequest(socketRef.current, "stopScreenShare", {
                meetId: meetStateRef.current.meetId, 
                userKey: meetStateRef.current.currentUser.userKey, 
                userId: meetStateRef.current.currentUser.userId,
                contentShareKey: meetStateRef.current.currentUser.contentShareKey,
            });
        }    
    }

    const startRecording = () => {
        if(socketRef.current){
            setRecordingByUser({
                meetId: meetStateRef?.current?.meetId,
                userId: meetStateRef?.current?.currentUser?.userId
            })
            sendRequest(socketRef.current, "startRecording", {
                meetId: meetStateRef.current?.meetId, 
                userKey: meetStateRef.current?.currentUser.userKey, 
                userId: meetStateRef.current?.currentUser.userId,
                userName: meetStateRef.current?.currentUser.userName
            });
        }
    }

    const stopRecording = () => {
        setRecordingByUser(null);
        if(socketRef.current) {
            sendRequest(socketRef.current, "stopRecording", {
                meetId: meetStateRef.current?.meetId, 
                userKey: meetStateRef.current?.currentUser.userKey, 
                userId: meetStateRef.current?.currentUser.userId,
                userName: meetStateRef.current?.currentUser.userName
            });
        }
    }

    const sendMessage = (message: string) => {
        if(socketRef.current && meetStateRef.current){
            const {meetId, currentUser} = meetStateRef.current
            sendRequest(socketRef.current, "sendMessage", {meetId, userKey: currentUser.userKey, userName: currentUser.userName, message, read: isChatOpenRef.current});
        }
    }

    const endMeeting =async (role: any, state: any, navigate:any, meetComponent: any, locationState?: any) => {
        if(isHost && socketRef.current && meetStateRef.current){
            setVideoRecordUploaded(true);
            await stopScreenShare();
            presenterRef.current = null;
            setPresenter(presenterRef.current);
            setTimeout(() => {
                if(socketRef.current && meetStateRef.current) {
                    sendRequest(socketRef.current, "endMeeting", {meetId: meetStateRef.current.meetId, userId: meetStateRef.current.currentUser.userId});
                }
            }, 1000);
            setTimeout(() => {
                resetAllValues(role, state, navigate, meetComponent, locationState);
            },1000);
        }
    }

    const InitSocket = async(meetType: FormType) => {
        window.addEventListener("beforeunload", () => {
            leaveMeeting();
        });
        window.addEventListener("popstate", () => {
            leaveMeeting();
        });

        if(meetStateRef.current && socketRef.current){
            
            const meetState = meetStateRef.current;
            let socket = socketRef.current;
            socket.onopen = () => {
                if(!socketExistsRef.current){
                    insertSendRequest({
                        variables: {
                            input: {
                                mediaserverErrorLog: {
                                    errorInfo: {
                                        meetId: meetState.meetId
                                    },
                                    errorType: 'getRtpCapabilities',
                                    userId: meetState?.currentUser?.userId,
                                    meetId: meetState?.meetId,
                                    status: 'pending'
                                }
                            }
                        }
                    }).then((data:any) => {
                        console.log("data is", data);
                        sendRequest(socket, "getRtpCapabilities", {meetId: meetState.meetId, requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id});
                    }).catch((err) => {
                        error(err);
                    })
                    sendRequest(socket, "initialiseAudioListeners", {meetId: meetState.meetId});
                    socketExistsRef.current = true;
                }
                // if((producerDisconnectedRef.current || consumerDisconnectedRef.current || screenDisconnectedRef.current) && socketRef.current && meetStateRef.current){
                //     sendRequest(socketRef.current, `restartIceProducer`, {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey});
                //     sendRequest(socketRef.current, `restartIceConsumer`, {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey});
                //     sendRequest(socketRef.current, `restartIceScreen`, {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey});
                // }
            }

            socket.onclose = async (event: any) => {
                const {newSocket} = await InitialiseConnection();
               if(newSocket){
                socketRef.current = newSocket;
                socket = socketRef.current;
               }
                // const {newSocket, errorMessage} = InitialiseConnection();
                // if(newSocket){
                //     socketRef.current = newSocket;
                //     InitSocket(meetType);
                // } else{
                //     error(errorMessage);
                // }
            }

            const device = new Device();

            socket.onmessage = async(event: MessageEvent) => {
                const message = JSON.parse(event.data);
                console.log("New Message : ", message);
                const type = message.type;
                const response = message.response;
                switch(type){
                    case "failed":
                        if(response.message?.length > 0 && response.requestId){
                            getErrorDataById({
                                variables: {
                                    id: response?.requestId
                                }
                            }).then((data:any) => {
                                updateErrorData({
                                    variables: {
                                        input: {
                                            id: data?.data?.allMediaserverErrorLogs?.nodes?.[0]?.id,
                                            mediaserverErrorLogPatch: {
                                                errorDescription: response?.message,
                                                status: 'failed'
                                            }
                                        }
                                    }
                                })
                            }).catch((err) => {
                                error(err);
                            })
                            error(response.message);
                        }else if(response?.message?.length > 0){
                            error(response.message);
                        }
                    break;

                    case "recordingStarted": 
                        if(response.meetId){
                            console.log("recording started", response);
                            setIsRecording(true);
                            setRecordingStartedTime(response.startedAt);
                        }
                    break;

                    case "recordingStopped": 
                        if(response.meetId){
                            console.log("recording ended");
                            setIsRecording(false);
                        }
                    break;

                    case "rtpCapabilities": 
                        if(meetState.meetId === response.meetId) {
                            try{
                                // routerRtpCapabilitiesref.current = response.rtpCapabilities; 
                                await device.load({routerRtpCapabilities: response.rtpCapabilities}).then(async () => {
                                    await insertSendRequest({
                                        variables: {
                                            input: {
                                                mediaserverErrorLog: {
                                                    errorInfo: {
                                                        forceTcp: false, 
                                                        rtpCapabilities: device,
                                                        meetId: meetState.meetId, 
                                                        userKey: meetState?.currentUser.userKey
                                                    },
                                                    errorType: 'createProducerTransport',
                                                    userId: meetState?.currentUser?.userId,
                                                    meetId: meetState?.meetId,
                                                    status: 'pending'
                                                }
                                            }
                                        }
                                    }).then((data:any) => {
                                        console.log("data is", data);
                                        sendRequest(socket, "createProducerTransport", {
                                            forceTcp: false, 
                                            rtpCapabilities: device,
                                            meetId: meetState.meetId, 
                                            userKey: meetState?.currentUser.userKey,
                                            requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id
                                        });
                                    }).catch((err) => {
                                        error(err);
                                    })
                                    
                                    await insertSendRequest({
                                        variables: {
                                            input: {
                                                mediaserverErrorLog: {
                                                    errorDescription: '',
                                                    errorInfo: {
                                                        meetId: meetState.meetId, 
                                                        userKey: meetState.currentUser.userKey
                                                    },
                                                    errorType: 'createConsumerTransport',
                                                    userId: meetState?.currentUser?.userId,
                                                    meetId: meetState?.meetId,
                                                    status: 'pending'
                                                }
                                            }
                                        }
                                    }).then((data:any) => {
                                        console.log("data is", data);
                                        sendRequest(socket, "createConsumerTransport", {
                                            meetId: meetState.meetId, 
                                            userKey: meetState.currentUser.userKey,
                                            requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id
                                        });
                                    }).catch((err) => {
                                        error(err);
                                    })



                                    await insertSendRequest({
                                        variables: {
                                            input: {
                                                mediaserverErrorLog: {
                                                    errorDescription: '',
                                                    errorInfo: {
                                                        forceTcp: false, 
                                                        rtpCapabilities: device,
                                                        meetId: meetState.meetId, 
                                                        userKey: meetState?.currentUser.userKey
                                                    },
                                                    errorType: 'createScreenTransport',
                                                    userId: meetState?.currentUser?.userId,
                                                    meetId: meetState?.meetId,
                                                    status: 'pending'
                                                }
                                            }
                                        }
                                    }).then((data:any) => {
                                        console.log("data is", data, data?.createMediaserverErrorLog, data?.createMediaserverErrorLog?.mediaserverErrorLog,  data?.createMediaserverErrorLog?.mediaserverErrorLog?.id);
                                        sendRequest(socket, "createScreenTransport", {
                                            forceTcp: false, 
                                            rtpCapabilities: device,
                                            meetId: meetState.meetId, 
                                            userKey: meetState?.currentUser.userKey,
                                            requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id
                                        });
                                    }).catch((err) => {
                                        error(err);
                                    })
                                    
                                    
                                    deviceRef.current = device;
                                });
                            }catch(err){

                            }
                        }
                    break;

                    case "newUser":
                            if(response.user){
                                const {userId, avatarKey, userKey, userName, preference, producer, avatar} = response.user;

                                if (participantsRef.current) {
                                    // Check if the participant with the same userId exists
                                    const existingUserKey = Object.keys(participantsRef.current).find(
                                        (key) => participantsRef.current?.[key].userId === userId
                                    );

                                    if (existingUserKey) {
                                        // Remove the existing participant with the same userId
                                        delete participantsRef.current[existingUserKey];
                                    }
                                }

                                participantsRef.current = {...participantsRef.current, [response.user.userKey]: {
                                    userKey,
                                    userId,
                                    userName,
                                    avatarKey,
                                    preference,
                                    producer,
                                    avatar,
                                    isPinned: {video: false, screen: false},
                                    remoteStream: {},
                                    screenShare: {}
                                }};
                                console.log("partcipants ref is",participantsRef);
                                console.log("participants are", participants);
                                if(userId !== meetState.currentUser.userId){
                                    await insertSendRequest({
                                        variables: {
                                            input: {
                                                mediaserverErrorLog: {
                                                    errorInfo: {
                                                        kind: "video",
                                                        rtpCapabilities: device.rtpCapabilities, 
                                                        producerId: producer.videoId, 
                                                        producerUserKey: userKey, 
                                                        meetId: meetState.meetId, 
                                                        userKey: meetState.currentUser.userKey
                                                    },
                                                    errorType: 'startConsume-video',
                                                    userId: meetState?.currentUser?.userId,
                                                    meetId: meetState?.meetId,
                                                    status: 'pending'
                                                }
                                            }
                                        }
                                    }).then((data:any) => {
                                        console.log("data is", data);
                                        sendRequest(socket, "startConsume", {
                                            kind: "video",
                                            rtpCapabilities: device.rtpCapabilities, 
                                            producerId: producer.videoId, 
                                            producerUserKey: userKey, 
                                            meetId: meetState.meetId, 
                                            userKey: meetState.currentUser.userKey,
                                            requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id
                                        });
                                    }).catch((err) => {
                                        error(err);
                                    })

                                    await insertSendRequest({
                                        variables: {
                                            input: {
                                                mediaserverErrorLog: {
                                                    errorInfo: {
                                                        kind: "audio",
                                                        rtpCapabilities: device.rtpCapabilities, 
                                                        producerId: producer.audioId, 
                                                        producerUserKey: userKey, 
                                                        meetId: meetState.meetId, 
                                                        userKey: meetState.currentUser.userKey,
                                                    },
                                                    errorType: 'startConsume-audio',
                                                    userId: meetState?.currentUser?.userId,
                                                    meetId: meetState?.meetId,
                                                    status: 'pending'
                                                }
                                            }
                                        }
                                    }).then((data:any) => {
                                        console.log("data is", data);
                                        sendRequest(socket, "startConsume", {
                                            kind: "audio",
                                            rtpCapabilities: device.rtpCapabilities, 
                                            producerId: producer.audioId, 
                                            producerUserKey: userKey, 
                                            meetId: meetState.meetId, 
                                            userKey: meetState.currentUser.userKey,
                                            requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id
                                        });
                                    }).catch((err) => {
                                        error(err);
                                    })
                                }
                            }   
                    break;

                    case "removeUser": 
                        if(response.meetId && participantsRef.current?.[response.user.userKey]){
                            delete participantsRef.current?.[response.user.userKey];
                            setParticipants(participantsRef.current);
                        }
                    break;

                    case "endMeeting":
                        console.log("meeting ended");
                        setVideoRecordUploaded(true);
                    break;

                    case "newMessage":
                        if(response.newMessage){
                            const newMessage: MessageType = {
                                [response.newMessage.messageKey]: {
                                    userKey: response.newMessage.userKey,
                                    userName: response.newMessage.userName,
                                    message: response.newMessage.message,
                                    createdAt: response.newMessage.createdAt,
                                    read: isChatOpenRef.current
                                }
                            };
                            chatMessageRef.current = {...chatMessageRef.current, ...newMessage};
                            setChatMessage(chatMessageRef.current);                       
                        }
                    break;

                    case "videoToggled":
                        
                        if(meetStateRef.current && response.userKey === meetStateRef.current?.currentUser.userKey){
                            meetState.currentUser.preference = {...meetState.currentUser.preference, video: response.video}
                            meetStateRef.current.currentUser.preference = {...meetState.currentUser.preference, video: response.video}
                            setVideo(response.video);
                        }
                    break;

                    case "updatedProducerVideoId":
                        if(response.user.userId !== meetStateRef.current?.currentUser.userId && participantsRef.current && participantsRef.current[response.user.userKey] && response.user.key !== "screenId"){
                            const participants = { ...participantsRef.current };
                            participants[response.user.userKey].producer.videoId = response.user.videoId;
                            sendRequest(socket, "startConsume", {
                                kind: "video",
                                rtpCapabilities: device.rtpCapabilities, 
                                producerId: response.user.videoId, 
                                producerUserKey: response.user.userKey, 
                                meetId: meetState.meetId, 
                                userKey: meetState.currentUser.userKey
                            });
                            participantsRef.current = participants;
                            setParticipants({...participants});
                        }
                    break;

                    case "audioToggled":
                        if(meetStateRef.current && response.userKey === meetStateRef.current?.currentUser.userKey){
                            meetState.currentUser.preference = {...meetState.currentUser.preference, audio: response.audio}
                            meetStateRef.current.currentUser.preference = {...meetState.currentUser.preference, audio: response.audio}
                            setAudio(response.audio);
                        }
                    break;

                    case "handRaiseToggled":
                        if(meetStateRef.current && response.userKey === meetStateRef.current?.currentUser.userKey){
                            meetState.currentUser.preference = {...meetState.currentUser.preference, hand: response.hand}
                            meetStateRef.current.currentUser.preference = {...meetState.currentUser.preference, hand: response.hand}
                            setHand(response.hand);
                        }
                    break;

                    case "mutedAllUsers":
                    break;

                    case "updatedPreference":
                        if(response.user.userKey && participantsRef.current?.[response.user.userKey]){
                            const participants = { ...participantsRef.current };

                            const audioEnabled = response?.user?.preference?.audio;
                            const handRaised = response?.user?.preference?.hand;
                            if (typeof audioEnabled === "boolean" || typeof handRaised === "boolean") {
                                console.log("[Log] Audio preference:", audioEnabled, participants[response.user.userKey]);
                                console.log("[Log] Hand preference:", handRaised, participants[response.user.userKey]);
                                participants[response.user.userKey].updatedAt = new Date().getTime();
                            }

                            participants[response.user.userKey].preference = { ...participants[response.user.userKey].preference, ...response.user.preference}
                            participantsRef.current = participants;
                            setParticipants({ ...participants });
                            updatePresenter(response.user);
                        }
                        if(meetStateRef.current && meetStateRef.current.currentUser.userId === response.user.userId && Object.keys(response.user.preference)[0] === "audio"){
                            meetState.currentUser.preference = {...meetState.currentUser.preference, ...response.user.preference}
                            meetStateRef.current.currentUser.preference = {...meetStateRef.current.currentUser.preference, ...response.user.preference}
                            setAudio(false);
                        }
                    break;

                    case "presentersCount":
                        console.log("response count is", response.count)
                        if(response.count <3){
                            if(presenterRef.current && presenterRef.current.pinType.screen){
                            console.log("presenter ref vale is in If condition", presenterRef.current);
                                setDialogOpen(true);
                            }else{
                            console.log("presenter ref vale is in else condition", presenterRef.current);
                                if(window.electron && window.electron.isElectron()){
                                    setOpenShareScreenPopup(true);
                                }else {
                                    startScreenShare();
                                }
                            }
                        }else{
                            console.log("error occured while 4 users");
                            error("Limit Exceed:3, Can't screen share now.");
                        }
                    break;

                    case "screenVideoProduced":
                        if(meetStateRef.current){
                            meetState.currentUser.preference.screen = true;
                            meetStateRef.current.currentUser.preference.screen = true;
                            setIsScreenSharing(true);
                        }
                    break;

                    case "newScreenShare":
                        if(response.screenShareInfo.userKey === meetState.currentUser.userKey && meetStateRef.current){
                            meetState.currentUser.contentShareKey = response.screenShareInfo.contentShareKey;
                            meetStateRef.current.currentUser.contentShareKey = response.screenShareInfo.contentShareKey;
                            meetStateRef.current.currentUser.contentShareId = response.screenShareInfo.screenId; //new line
                        }
                        if(response.screenShareInfo.userKey !== meetState.currentUser.userKey && participantsRef.current?.[response.screenShareInfo.userKey] && response.screenShareInfo.screenId){
                            const participants = { ...participantsRef.current };
                            sendRequest(socket, "consumeScreenShare", {
                                kind: "video",
                                rtpCapabilities: device.rtpCapabilities, 
                                producerId: response.screenShareInfo.screenId, 
                                producerUserKey: response.screenShareInfo.userKey, 
                                meetId: meetState.meetId, 
                                userKey: meetState.currentUser.userKey
                            });
                            participants[response.screenShareInfo.userKey].screenShare.screenId = response.screenShareInfo.screenId;
                            participants[response.screenShareInfo.userKey].preference.screen = true;
                            participantsRef.current = participants;
                            setParticipants({ ...participants });
                        }
                    break;

                    case "endScreenShare":
                        if(response.screenShareInfo.userKey === meetState.currentUser.userKey && meetStateRef.current){
                            meetState.currentUser.preference.screen = false;
                            meetStateRef.current.currentUser.preference.screen = false;
                           
                            let presenter: MainTileType | null = null;
                            if(participantsRef.current){
                                Object.keys(participantsRef.current).forEach((key) => {
                                    if(participantsRef.current && participantsRef.current[key]){
                                        const user = participantsRef.current?.[key];
                                        if(user.preference.screen && user.screenShare.displayStream){
                                            presenter = {
                                                userId: user.userId,
                                                userName: user.userName,
                                                videoStream: user.screenShare.displayStream,
                                                title: `${user.userName} is presenting`,
                                                pinType: {
                                                    screen: true,
                                                    video: false
                                                },
                                                preference: user.preference
                                            }
                                        }
                                    }
                                });
                            }
                            presenterRef.current = presenter;
                            setPresenter(presenter);
                        }
                        if(response.screenShareInfo.userKey !== meetState.currentUser.userKey && participantsRef.current?.[response.screenShareInfo.userKey] ){
                            const participants = { ...participantsRef.current };
                            participants[response.screenShareInfo.userKey].preference.screen = false;
                            participantsRef.current = participants;
                            setParticipants({ ...participants });
                            let presenter: MainTileType | null = null;
                          
                            if(meetStateRef.current && meetStateRef.current.currentUser.preference.screen && displayStreamRef.current){
                                presenter = {
                                    userId: meetStateRef.current.currentUser.userId,
                                    userName: meetStateRef.current.currentUser.userName,
                                    videoStream: displayStreamRef.current,
                                    title: YOU_ARE_PRESENTING_NOW,
                                    pinType: {
                                        screen: true,
                                        video: false
                                    },
                                    preference: meetStateRef.current.currentUser.preference
                                }
                            }else{
                                if(participantsRef.current){
                                    Object.keys(participantsRef.current).forEach((key) => {
                                        if(participantsRef.current && participantsRef.current[key]){
                                            const user = participantsRef.current?.[key];
                                            if(user.preference.screen && user.screenShare.displayStream){
                                                presenter = {
                                                    userId: user.userId,
                                                    userName: user.userName,
                                                    videoStream: user.screenShare.displayStream,
                                                    title: `${user.userName} is presenting`,
                                                    pinType: {
                                                        screen: true,
                                                        video: false
                                                    },
                                                    preference: user.preference
                                                }
                                            }
                                        }
                                    });
                                }
                            }
                            presenterRef.current = presenter;
                            setPresenter(presenter);
                        }

                    break;
                    
                    case "screenShareConsumed":
                        if(consumerTransport.current){
                            const data = {
                                consumerId: response.data.consumerId,
                                producerId: response.data.producerId,
                                kind: response.data.kind,
                                rtpParameters: response.data.rtpParameters
                            }
                            try{
                                await consumerTransport.current.consume({
                                    id: data.consumerId,
                                    producerId: data.producerId,
                                    kind: data.kind,
                                    rtpParameters: data.rtpParameters
                                }).then( (consumer) => {
                                    switch(consumer.kind){
                                        case "video": 
                                            const videostream = new MediaStream();
                                            videostream.addTrack(consumer.track);
                                            if(response.producerUserKey && participantsRef.current?.[response.producerUserKey]){
                                                const participants = { ...participantsRef.current };
                                                participants[response.producerUserKey].screenShare.displayStream = videostream;
                                                participants[response.producerUserKey].isPinned = {screen: true, video: false};
                                                participantsRef.current = participants;
                                                setParticipants({ ...participants });
                                                const presenter = {
                                                    userId: participants[response.producerUserKey].userId,
                                                    userName: participants[response.producerUserKey].userName,
                                                    videoStream: videostream,
                                                    title: `${participants[response.producerUserKey].userName} is presenting`,
                                                    pinType: {
                                                        screen: true,
                                                        video: false
                                                    },
                                                    preference: participants[response.producerUserKey].preference
                                                };
                                                setPresenter(presenter);
                                                presenterRef.current = presenter;
                                            }
                                        break;
                                    }
                                });
                            }catch(error){
                                console.log("Consume Error : ", error);
                            }
                            
                        }
                    break;

                    case "screenTransportCreated":
                        const screenProducer = await onProducerTransportCreated(response.params, device);
                        screenTransport.current = screenProducer;
                        await InitialiseScreenTransportListener(screenProducer, socket, meetState.meetId, meetState.currentUser);
                        // screenTransport.current.on("connectionstatechange", (state) => {
                        //     console.log("screen connection change: ", state);
                        //     switch(state) {
                        //         case "connected": 
                        //             screenDisconnectedRef.current = true;
                        //         break;

                        //         case "disconnected":
                        //             // screenDisconnectedRef.current = true;
                        //             // handleNetworkChange('Screen');
                        //         break;
                    
                        //         case "failed":
                        //             screenDisconnectedRef.current = true;
                        //         break;
                        //     }
                        // })
                    break;


                    case "producerTransportCreated": 
                    const producer = await onProducerTransportCreated(response.params, device);
                    producerTransport.current = producer;
                    const stream = await InitialiseProducerTransportListener(producer, socket, device, meetState.meetId, meetState.currentUser);
                    console.log("the stream variable is", stream);
                    producerTransport.current.on("connectionstatechange", (state) => {
                        console.log("connectionstatechange : ", state)
                        switch(state){
                            case "connected":
                                // producerDisconnectedRef.current = false;
                                // setIsOnline(true);
                            if(stream) {
                                setLocalStream(stream);
                            }
                            break;

                            case "disconnected":
                                // producerDisconnectedRef.current = true;
                                // handleNetworkChange('Producer');
                                // setIsOnline(false);
                            break;
                
                            case "failed":
                                // producerDisconnectedRef.current = true;
                                // setIsOnline(false);
                            break;
                        }
                    });
                break;

                case "producerTransportUpdated":
                    const updateProducer = await onProducerTransportCreated(response.params, device, routerRtpCapabilitiesref.current);
                    producerTransport.current = updateProducer;
                    const updateStream = await InitialiseUpdateProducerTransportListener(updateProducer, socket, device, meetState.meetId, meetState.currentUser);
                    producerTransport.current.on("connectionstatechange", (state) => {
                        console.log("connectionstatechange : ", state)
                        switch(state){
                            case "connected":
                                // setIsOnline(true);
                                // producerDisconnectedRef.current = false;
                                if(updateStream) {
                                    setLocalStream(updateStream);
                                }
                            break;
                            case "disconnected":
                                // producerDisconnectedRef.current = true;
                                // handleNetworkChange('Producer');
                                // setIsOnline(false);
                            break;
                            case "failed": 
                                // producerDisconnectedRef.current = true;
                                // setIsOnline(false);
                            break;
                        }
                    });
                break;

                    case "consumerTransportCreated":
                        const consumer = await onConsumerTransportCreated(response.params, device); //consumerTransport
                        consumer.on( "connect", async ({dtlsParameters}, callback) => {
                            await insertSendRequest({
                                variables: {
                                    input: {
                                        mediaserverErrorLog: {
                                            errorInfo: {
                                                transportId: consumer.id, 
                                                dtlsParameters, meetId: meetState.meetId, 
                                                userKey: meetState.currentUser.userKey, 
                                            },
                                            errorType: 'connectConsumerTransport',
                                            userId: meetState?.currentUser?.userId,
                                            meetId: meetState?.meetId,
                                            status: 'pending'
                                        }
                                    }
                                }
                            }).then((data:any) => {
                                console.log("data is", data);
                                sendRequest(socket, "connectConsumerTransport", {transportId: consumer.id, dtlsParameters, meetId: meetState.meetId, userKey: meetState.currentUser.userKey, requestId: data?.data?.createMediaserverErrorLog?.mediaserverErrorLog?.id});
                            }).catch((err) => {
                                error(err);
                            })
                            
                            socket.addEventListener( "message",  (event: MessageEvent) => {
                                const data = JSON.parse(event.data);
                                if(data.type === "consumerConnected"){
                                    callback();
                                }
                            });
                        });
                        consumerTransport.current = consumer;
                        consumerTransport.current.on("connectionstatechange", (state) => {
                            console.log("connectionstatechange consumer : ", state)
                            switch(state){
                                case "connected":
                                    // setIsOnline(true);
                                    // consumerDisconnectedRef.current = false;
                                break;
                                case "disconnected":
                                    // consumerDisconnectedRef.current = true;
                                    // setIsOnline(false);
                                    // handleNetworkChange('Consumer');
                                break;
                                case "failed":
                                    // consumerDisconnectedRef.current = true;
                                    // setIsOnline(false);
                                break;
                            }
                        })

                    break;

                    case "consumed":
                    if(consumerTransport.current){
                        const data = {
                            consumerId: response.data.consumerId,
                            producerId: response.data.producerId,
                            kind: response.data.kind,
                            rtpParameters: response.data.rtpParameters
                        }
                        try{
                            await consumerTransport.current.consume({
                                id: data.consumerId,
                                producerId: data.producerId,
                                kind: data.kind,
                                rtpParameters: data.rtpParameters
                            }).then( (consumer) => {
                                switch(consumer.kind){
                                    case "video": 
                                        const videostream = new MediaStream();
                                        videostream.addTrack(consumer.track);
                                        if(response.producerUserKey && participantsRef.current?.[response.producerUserKey]){
                                            const participants = { ...participantsRef.current };
                                            participants[response.producerUserKey].remoteStream.video = videostream;
                                            participantsRef.current = participants;
                                            setParticipants({ ...participants });
                                        }
                                    break;
                                    case "audio":
                                        const audiostream = new MediaStream();
                                        audiostream.addTrack(consumer.track);
                                        if(response.producerUserKey && participantsRef.current?.[response.producerUserKey]){
                                            const participants = { ...participantsRef.current };
                                            participants[response.producerUserKey].remoteStream.audio = audiostream;
                                            participantsRef.current = participants;
                                            setParticipants({ ...participants });
                                        }
                                    break;
                                }
                            });
                        }catch(error){
                            console.log("Consume Error : ", error);
                        }
                        
                    }
                break;

                    case "MeetingEnded":
                        console.log("meeting ended");
                        setParticipantsGridViewVisible(false);
                        setIsScreenSharing(false);
                        displayStreamRef.current = null;
                        setIsMeetingEndByHost(true);
                        resetAllValues();
                    break;

                    case "currentActiveSpeaker":
                        console.log("[Log] Active speaker", response);
                        if (response?.userKey && participantsRef?.current?.[response.userKey]) {
                            const participants = { ...participantsRef.current };
                            participants[response.userKey].updatedAt = new Date().getTime();
                            participantsRef.current = participants;
                            setParticipants({ ...participants });
                        }
                        break;

                    case "producerIceRestarted":
                        restartIce(response.iceParameters, 'producer');
                    break;

                    case "consumerIceRestarted":
                        restartIce(response.iceParameters, 'consumer');
                    break;

                    case "screenIceRestarted":
                        restartIce(response.iceParameters, 'consumer');
                    break;
                }
            }

            const restartIce = (iceParameters: MediasoupTypes.IceParameters, transportType: string) => {
                if(transportType === "producer"){
                    producerTransport.current?.restartIce({iceParameters});
                } else if(transportType === "consumer"){
                    consumerTransport.current?.restartIce({iceParameters});
                } else if(transportType === "screen"){
                    screenTransport.current?.restartIce({iceParameters});
                }
            }

            // const handleNetworkChange = async (transportType: string) => {
            //     window.addEventListener("online", async (e) => {
            //         if(socketRef.current?.readyState === 1 && meetStateRef.current){
            //             sendRequest(socketRef.current, `restartIce${transportType}`, {meetId: meetStateRef.current.meetId, userKey: meetStateRef.current.currentUser.userKey});
            //         }
            //     });
            //     window.addEventListener("offline", (e) => {
            //         console.log("offline", e);
            //     });
            // }
            
        }
    
    }


   
    return(
    <GlobalContext.Provider 
        value={{ 
            recordingStartedTime,
            recordingByUser,
            startRecording,
            stopRecording,
            setIsMeetingEndByHost,
            setParticipants,
            participantsRef,
            setProfileData,
            isMeetingEndByHost,
            presenterRef,
            setPresenter,
            muteAllUsers,
            muteUsers,
            hand,
            endMeeting,
            isHost,
            profileData,
            setVideoRecordUploaded,
            uploadedFileName,
            videoRecordUploaded,
            isRecording,
            setOpenShareScreenPopup,
            openShareScreenPopup,
            localStream, 
            toggleParticipant, 
            isParticipantsGridViewVisible,
            handleParticipantsGridViewToggle,
            isParticipantListView, 
            InitialiseMeeting, 
            meetStateRef, 
            participants, 
            toggleAudio, 
            toggleVideo, 
            toggleHandRaise,
            audio, 
            video, 
            presenter,
            dialogOpen,
            setDialogOpen,
            pinVideoToMainTile,
            isScreenSharing, 
            isAnyoneScreenSharing,
            displayStreamRef,
            chatMessages, 
            sendMessage, 
            isChatOpen, 
            toggleChat, 
            leaveMeeting, 
            handleScreenShare, 
            startScreenShare,
            stopScreenShare,
            isOnline,
        }}>
        {props.children}
    </GlobalContext.Provider>
   )
}